/**
 * Deep Link Types - Mirrors the backend DeepLinkType enum
 * Used for parsing incoming deep links and determining navigation targets
 */
export enum DeepLinkType {
  // Absence Management
  AbsenceApprove = 'AbsenceApprove',
  AbsenceView = 'AbsenceView',
  AbsenceList = 'AbsenceList',

  // Employee Management
  EmployeeProfile = 'EmployeeProfile',
  EmployeeEdit = 'EmployeeEdit',
  EmployeeChangeRequest = 'EmployeeChangeRequest',

  // Shift Planning
  ShiftPlan = 'ShiftPlan',
  ShiftDetail = 'ShiftDetail',
  ShiftSwapRequest = 'ShiftSwapRequest',

  // Time Recording
  TimeRecordingList = 'TimeRecordingList',
  TimeRecordingEdit = 'TimeRecordingEdit',
  TimeChangeRequest = 'TimeChangeRequest',

  // Dashboard
  DashboardOverview = 'DashboardOverview',

  // Authentication
  AcceptInvitation = 'AcceptInvitation',
  PasswordReset = 'PasswordReset',
  EmailConfirmation = 'EmailConfirmation',

  // Settings
  Settings = 'Settings',
  DatevExport = 'DatevExport',

  // Alerts
  OpenClockInsAlert = 'OpenClockInsAlert',
  ComplianceAlert = 'ComplianceAlert',
  OvertimeAlert = 'OvertimeAlert',

  // General
  Login = 'Login',
  Unknown = 'Unknown'
}

/**
 * Parsed deep link data
 */
export interface ParsedDeepLink {
  /** The detected type of deep link */
  type: DeepLinkType;
  /** The frontend route path (e.g., '/absence') */
  path: string;
  /** Query parameters from the URL */
  params: Record<string, string>;
  /** Original URL for debugging */
  originalUrl: string;
}

/**
 * Deep link handler result
 */
export interface DeepLinkResult {
  /** Whether the deep link was successfully handled */
  success: boolean;
  /** The path that was navigated to */
  navigatedTo?: string;
  /** Error message if handling failed */
  error?: string;
  /** Whether auth redirect was required */
  authRedirectRequired?: boolean;
}

/**
 * Configuration for deep link handling
 */
export interface DeepLinkConfig {
  /** Routes that require authentication */
  protectedRoutes: string[];
  /** Routes that are public (no auth required) */
  publicRoutes: string[];
  /** Storage key for pending deep link after auth */
  pendingLinkStorageKey: string;
}

/**
 * Default deep link configuration
 */
export const DEFAULT_DEEP_LINK_CONFIG: DeepLinkConfig = {
  protectedRoutes: [
    '/absence',
    '/employee',
    '/planner',
    '/time-recording',
    '/dashboard',
    '/settings'
  ],
  publicRoutes: [
    '/login',
    '/accept-invitation',
    '/resetpassword',
    '/email-confirmation',
    '/terminal-pairing'
  ],
  pendingLinkStorageKey: 'fixplaner_pending_deep_link'
};
